import { useToast } from "@chakra-ui/react"
import { ComponentProps, FC, useCallback, useEffect, useState } from "react"
import { Registration } from "src/domain/entities/registration"
import { DomainError } from "src/domain/errors"
import { useAuth, useSurnameListService } from "src/domain/hooks"
import { registrationBasicUpdateService } from "src/domain/services/registration/registrationBasicUpdateService"
import { FormikOnSubmit } from "src/utils/types"
import { IRegistrationBasicUpdateFormFields } from "./IRegistrationBasicUpdateFormFields"
import { RegistrationBasicUpdateDrawerFormView } from "./RegistrationBasicUpdateDrawerFormView"

type Props = Omit<
	ComponentProps<typeof RegistrationBasicUpdateDrawerFormView>,
	"handleSubmit" | "surnameList" | "surnameSearchText" | "setSurnameSearchText"
> & {
	onSuccess?: (registrationBasic: Registration) => void
}

export const RegistrationBasicUpdateDrawerFormController: FC<Props> = ({
	registration,
	onSuccess,
	...rest
}) => {
	const { token } = useAuth()
	const toast = useToast()

	const handleSubmit = useCallback<FormikOnSubmit<IRegistrationBasicUpdateFormFields>>(
		async (values) => {
			try {
				if (!registration) return

				const updatedState = await registrationBasicUpdateService(
					{ id: registration.id, update: values },
					token,
				)
				onSuccess && onSuccess(updatedState)
				rest.onClose()
			} catch (err) {
				if (err instanceof DomainError) {
					toast({
						title: err.name,
						description: err.message,
						status: "error",
						position: "bottom-right",
						isClosable: true,
						duration: 10000,
					})
				}
			}
		},
		[onSuccess, rest, token, registration, toast],
	)
	const { fetchSurnameList, surnameList } = useSurnameListService()

	const [surnameSearchText, setSurnameSearchText] = useState<string>("")

	useEffect(() => {
		fetchSurnameList({
			search: surnameSearchText || registration.basic?.surname?.name.en,
			pagination: {
				page: 1,
				limit: 20,
			},
			subCasteId:
				registration?.basic?.surname?.subCaste?.id ??
				registration?.basic?.customSurname?.subCaste?.id,
		})
	}, [fetchSurnameList, surnameSearchText, registration])

	return (
		<RegistrationBasicUpdateDrawerFormView
			registration={registration}
			surnameList={surnameList}
			handleSubmit={handleSubmit}
			surnameSearchText={surnameSearchText}
			setSurnameSearchText={setSurnameSearchText}
			{...rest}
		/>
	)
}
